import { RoundedFilledButton } from '@/components/buttons/Buttons';
import LoadingBlocker from '@/components/dialog/LoadingBlocker';
import { showNotification } from '@/modules/notification/engine/notification.library';
import IdGeneratorService from '@/services/id-generator.service';
import { Dialog  } from 'primereact/dialog';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdOutlineQueue } from 'react-icons/md';
import styles from './request-budget-dialog.module.css';
import CustomInputTextarea from '@/components/form-inputs/CustomInputTextarea';
import { number, object, string } from 'yup';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useModal } from '@ebay/nice-modal-react';
import { ENDPOINTS } from '@/constants/endpoints';
import { BaseResponse } from '@/types';
import { axiosInstance } from '@/services/axios.service';
import NumberInput from '@/components/inputs/NumberInput';
import { InputController } from '@/components/inputs/InputController';

export type CreateBudgetRequest = {
	Amount: number;
	Reason: string;
}

export const schema = object({
	Reason: string().trim().required(VALIDATION_MESSAGES.required),
	Amount: number().required(VALIDATION_MESSAGES.required)
});

type Props = {
};

const RequestBudgetDialog = (props: Props) => {
	const [formBlocked, setFormBlocked] = useState(false);

	const formId = 'request-budget';
	const form = useForm<CreateBudgetRequest>({
		resolver: yupResolver(schema),
		mode: 'all'
	});
	const modal = useModal();



	return (
		<Dialog
			className={styles.dialog}
			closable={false}
			draggable={false}
			visible={true}
			focusOnShow={false}
			onHide={() => {
				modal.remove();
			}}
		>
			<div className="border-b-[1px] border-solid border-black px-2">
				<div className="flex items-center gap-[6px]">
					<MdOutlineQueue size="24" />
					<h1 className="text-2xl">Request Budget</h1>
				</div>

				<p className="mt-4 mb-6">
					To proceed with this request, please provide the necessary
					budget details.
				</p>
			</div>

			<div className="px-2 py-6">
				<LoadingBlocker
					blocked={formBlocked}
					blockTemplate="Sending request..."
					pt={{
						mask: {
							className: styles.loadingMask,
						},
					}}
				>
					<form
						id={new IdGeneratorService(formId).formId}
						action=""
						className="flex flex-col gap-6"
					>
						<InputController id="Amount" control={form.control}>
							<NumberInput
								label="Amount*"
								placeholder="Enter amount"
							/>
						</InputController>

						<CustomInputTextarea
							id="Reason"
							label="Reason*"
							placeholder="Enter reason"
							formName={formId}
							control={form.control}
							rows={1}
							autoResize
						/>
					</form>
				</LoadingBlocker>

				<div className={styles.footer}>
					<RoundedFilledButton
						className={styles.cancelButton}
						label="Cancel"
						id="cancel-request-button"
						onClick={() => {
							modal.remove();
						}}
					/>
					<RoundedFilledButton
						className={styles.requestButton}
						label="Request"
						id="request-button"
						disabled={!form.formState.isValid}
						onClick={form.handleSubmit(async (data) => {
							const notificationMessage = 'Request sent';
							setFormBlocked(true);
							try {
								await axiosInstance.post<BaseResponse>(ENDPOINTS['/api/Budget/CreateBudgetRequest'], data);
								modal.resolve({});
								modal.remove();
								showNotification(notificationMessage);
							} finally {
								setFormBlocked(false);
							}

						}

						)}
					/>
				</div>
			</div>
		</Dialog>
	);
};

export default RequestBudgetDialog;
